import React, { useState, useEffect } from 'react';
import {
  HiX,
  HiHome,
  HiUserGroup,
  HiUser,
  HiCog,
  HiAcademicCap,
  HiChevronDown,
  HiChevronRight,
  HiLogout,
  HiShoppingBag,
  HiCreditCard,
  HiTicket,
  HiTruck,
  // HiOfficeBuilding,
} from 'react-icons/hi';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface MenuItem {
  path?: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  subItems?: { path: string; label: string }[];
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['강의 관리']);
  const { logout } = useAuthStore();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (location.pathname.includes('/courses') || location.pathname.includes('/instructors')) {
      setExpandedMenus((prev) => [...new Set([...prev, '강의 관리'])]);
    }
  }, [location.pathname]);

  const menuItems: MenuItem[] = [
    { path: '/dashboard', icon: HiHome, label: '대시보드' },
    { path: '/dashboard/admins', icon: HiUserGroup, label: '관리자' },
    { path: '/dashboard/users', icon: HiUser, label: '사용자' },
    // { path: '/dashboard/institutions', icon: HiOfficeBuilding, label: '기관 관리' },
    {
      icon: HiAcademicCap,
      label: '강의 관리',
      subItems: [
        { path: '/dashboard/courses', label: '강의 목록' },
        { path: '/dashboard/instructors', label: '강사 관리' },
      ],
    },
    { path: '/dashboard/products', icon: HiShoppingBag, label: '상품 관리' },
    { path: '/dashboard/payments', icon: HiCreditCard, label: '결제 관리' },
    { path: '/dashboard/enrollments', icon: HiTicket, label: '수강권 관리' },
    { path: '/dashboard/deliveries', icon: HiTruck, label: '배송 관리' },
    { path: '/dashboard/settings', icon: HiCog, label: '설정' },
  ];

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) => (prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]));
  };

  const CloseIcon = HiX as React.ComponentType<{ size?: number }>;

  const sidebarClasses = `fixed top-0 left-0 h-screen w-[280px] bg-slate-900 text-slate-300 transition-transform duration-300 ease-in-out z-[1000] flex flex-col border-r border-slate-800 ${
    isMobile ? (isOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full') : 'translate-x-0'
  }`;

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div className="animate-fade-in fixed inset-0 z-[999] bg-slate-900/60 backdrop-blur-sm" onClick={onToggle} />
      )}

      {/* Sidebar */}
      <aside className={sidebarClasses}>
        <div className="mb-2 flex items-center justify-between px-6 py-6">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Lumos Admin Logo" className="h-8 w-8 object-contain" />
            <h2 className="text-xl font-bold tracking-tight text-white">Admin</h2>
          </div>
          {isMobile && (
            <button className="p-1 text-slate-400 transition-colors hover:text-white" onClick={onToggle}>
              <CloseIcon size={24} />
            </button>
          )}
        </div>

        <nav className="custom-scrollbar flex-1 space-y-1 overflow-y-auto px-4 py-2">
          {menuItems.map((item) => {
            const Icon = item.icon as React.ComponentType<{ className?: string }>;
            const isExpanded = expandedMenus.includes(item.label);
            const isActive = item.path && location.pathname === item.path;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isSubItemActive = hasSubItems && item.subItems!.some((subItem) => location.pathname === subItem.path);

            return (
              <div key={item.path || item.label} className="mb-1">
                {hasSubItems ? (
                  <div className="space-y-1">
                    <button
                      onClick={() => toggleMenu(item.label)}
                      className={`group flex w-full items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 ${
                        isSubItemActive
                          ? 'bg-slate-800/50 text-white'
                          : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                      }`}>
                      <div className="flex items-center gap-3">
                        <Icon
                          className={`text-xl transition-colors ${isSubItemActive ? 'text-blue-400' : 'group-hover:text-blue-400'}`}
                        />
                        <span className="text-sm font-medium">{item.label}</span>
                      </div>
                      {isExpanded ? (
                        <HiChevronDown className="text-slate-500" />
                      ) : (
                        <HiChevronRight className="text-slate-500" />
                      )}
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <ul className="mt-1 space-y-1 pl-4">
                        {item.subItems!.map((subItem) => {
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <li key={subItem.path}>
                              <Link
                                to={subItem.path}
                                className={`flex items-center rounded-md border-l-2 px-3 py-2 text-sm transition-all duration-200 ${
                                  isSubActive
                                    ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                                    : 'border-transparent text-slate-500 hover:bg-slate-800/30 hover:text-slate-300'
                                }`}
                                onClick={() => isMobile && onToggle()}>
                                {subItem.label}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.path!}
                    className={`group flex items-center rounded-lg px-3 py-2.5 transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                    }`}
                    onClick={() => isMobile && onToggle()}>
                    <Icon
                      className={`mr-3 text-xl transition-colors ${isActive ? 'text-white' : 'group-hover:text-blue-400'}`}
                    />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-slate-800 p-4">
          <button
            onClick={logout}
            className="group flex w-full items-center rounded-lg px-3 py-2.5 text-slate-400 transition-all duration-200 hover:bg-red-500/10 hover:text-red-400">
            <HiLogout className="mr-3 text-xl transition-colors group-hover:text-red-400" />
            <span className="text-sm font-medium">로그아웃</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
